/**
 * `Studio` — l'accueil : la chambre, sa surcouche d'informations, et ses actions.
 * Avenant **A-7** (« la chambre est l'accueil »), qui remplace le tableau de bord en panneaux
 * de SPEC-UI §3.1 — **tout le contenu informationnel de §3.1 reste valable**, seul son
 * cadrage change.
 *
 * ⟦C21 · A-146⟧ **Direction F3** (choix de Mathieu du 12/09, docs/chantiers/
 * C21-le-depart-en-partie.md) : le cadrage change une deuxième fois. Le contenu de §3.1
 * (compteur, jauges, notifications, actions) est désormais réparti dans trois fenêtres pixel
 * réutilisables (`Fenetre.tsx`) — « Ma chaîne », « Journal », « Actions » — qui encadrent la
 * scène sur ordinateur et deviennent des onglets plein cadre sous 768 px (`mobile`, plus bas).
 * Matt (`useMattChambre`, `game/parole.ts`) y parle dans une `BoiteDialogue` posée au-dessus de
 * la fenêtre Actions, à la place des anciens `ModeDecouverte`/`InfoBulleDecouverte` (retirés de
 * CET écran par `App.tsx`, toujours actifs ailleurs).
 *
 * Les garde-fous de l'avenant A-7, toujours vrais, juste réhabillés :
 * - **décision 3** : les actions restent permanentes, dans la fenêtre « Actions » — toujours au
 *   même endroit, jamais un objet à chasser pour dormir.
 * - **décision 6** : jauges, tendances et notifications restent lisibles **en permanence**,
 *   dans « Ma chaîne »/« Journal » plutôt qu'en overlay flottant sur la scène.
 * - **décision 4** : les panneaux (Bibliothèque, Arbre, Journal, Nouvelle vidéo) s'ouvrent
 *   par-dessus — c'est l'`<Outlet/>` en bas de ce fichier, HORS du cadre pixel (jamais rogné).
 *
 * ⟦A-5⟧ Ce qui n'est pas déverrouillé n'existe pas : ni l'objet dans la chambre, ni le bouton
 * dans la fenêtre Actions. Seule exception, réclamée par **A-1 point 8** : la jauge Argent est
 * **grisée avec son tooltip**, pas cachée.
 *
 * ⟦C57 · lot 4 · A-174⟧ **La chambre, l'écran où l'on passe le plus de temps, en abrégé.** À 173 mots
 * visibles au milieu de partie (190 avec des alertes), on n'y lisait plus rien. Il reste : les
 * jauges en barres et en nombres, les gros chiffres, les boutons avec leurs coûts, et pour chaque
 * bandeau UNE phrase de 12 mots au plus. Ce qui part derrière « Les dessous » (le pictogramme 📰),
 * MOT POUR MOT : la ligne de surcharge, la fatigue et la zone rouge, la victoire gravée, le conseil
 * sous les boutons ; et, côté fenêtre « Journal », le détail de chaque fait (palier, jours écoulés…)
 * que sa ligne courte ne dit plus (`DessousDuJournal`, le mécanisme du lot 1).
 *
 * ⟦C60 · A-177⟧ **Le grand ménage : la chambre ne montre plus que ce qui reste.** Matt (03/10) : *« Il faut tout
 * simplifier, garder les grands principes. »* Neuf systèmes sont coupés par le contenu (`meta.systemes`, lu par
 * `game/menage.ts`, un seul lecteur) : ici, plus de bouton Bibliothèque, Boutique, Logement ni Jalons, plus
 * d'objet flottant dans la pièce (📚 l'étagère, 🏦 la boîte à Clicks, 📦 le carton), plus de loyer ni de
 * charges à lire, plus d'indicateur « Ma ligne ». Le compteur de Clicks reste (il paie le Marché, l'Équipe et
 * l'Arbre). Chaque bouton qui reste garde un objet dessiné de la chambre (`OBJET_PAR_BOUTON_ACTION`) : le
 * Marché et l'Équipe au bureau, l'Arbre au liège, le Fil au téléphone, la veille à la fenêtre. Le code des
 * systèmes coupés reste, derrière `sys.<système>` : rallumé, il revient tel quel. Rien n'est supprimé.
 */

import { useEffect, useState, type HTMLAttributes } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import {
  ENERGIE_MAX,
  H_EVEILLE_PAR_JOUR,
  H_JOUR,
  H_NUIT,
  OBJECTIF,
  PALIERS,
  PALIERS_POST_1M,
  SURCHARGE_DECAY_PAR_JOUR,
  actionCost,
  chargesDuJour,
  collectModifiers,
  equipeActive,
  evergreensActives,
  libelleNiche,
  marcheOuvert,
  nicheReconnue,
  plafondEnergie,
  producteurPossede,
  renteHebdoVues,
  rivauxLisibles,
  surchargeAccordee,
  type Action,
  type AtelierScores,
  type GameState,
} from "@maxime/shared";

import { AlertesChaine } from "../../components/AlertesChaine";
import { BoiteDialogue, type Interlocuteur } from "../../components/BoiteDialogue";
import { Fenetre } from "../../components/Fenetre";
import { Jauge } from "../../components/Jauge";
import { LogoMeTube } from "../../components/LogoMeTube";
import { LigneAvecDetail } from "../../components/DessousDuJournal";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { Concurrence, FenetresOuvertes } from "../../components/PressionsDuTemps";
import { OBJET_PAR_BOUTON_ACTION } from "../../game/chambre";
import { contenu } from "../../game/content";
import { aRetenirCourtDe } from "../../game/decouverte";
import { ouvert, prochainEtage } from "../../game/escalier";
import { poseHeros } from "../../game/heros";
import { cibleDansLeDetail, hudOuvert, hudVisible, lignesDuDetail, tableauLimpide, type ElementHud } from "../../game/hud";
import { capacite } from "../../game/moteur";
import { classesBulleMattMobile } from "../../game/parole";
import {
  heureDuJour,
  jourSimule,
  libelleCout,
  nombre,
  nombreCourt,
  points,
  zoneSM,
} from "../../game/format";
import { marche } from "../../game/marche";
import { raisonSansIdee } from "../NouvelleVideo/textes-courts";
import { interrupteurs } from "../../game/menage";
import { useAccueilStore } from "../../stores/accueilStore";
import { useDecouverteStore } from "../../stores/decouverteStore";
import { useRunStore } from "../../stores/runStore";
import { dialog, useUIStore } from "../../stores/uiStore";

import { BoutonLaisserTourner } from "../Equipe/BoutonLaisserTourner";

import { Chambre, IndicateurMaLigne, type ObjetChambre } from "./Chambre";
import { useControlesDeMattAuDessus } from "./controlesMatt";
import { useMattChambre } from "./useMattChambre";

/**
 * ⟦C21 · A-146⟧ Le seuil bureau/mobile de la direction F3 — même valeur que la maquette
 * (`assets/making-of/2026-09-12-espace2-chambre/maquettes/F3/_commun.css`, `@media (min-width:
 * 768px)`). `767.98px` plutôt que `767px` : même raison que `BandeauPetitEcran` — entre les
 * deux, une largeur de 767,5 px ne serait ni l'un ni l'autre.
 */
export const REQUETE_FENETRES_MOBILE = "(max-width: 767.98px)";

/** L'onglet mobile ouvert — `null` : la chambre respire, aucune fenêtre posée dessus. */
type OngletMobile = "chaine" | "journal" | "actions" | null;

/**
 * ⟦C21 · A-146 §2⟧ L'hôte de la boîte de Matt (C20, `_commun.css`) — même constante que
 * `NouvelleRun.tsx` (non exportée là-bas, retapée à l'identique, jamais un import cross-écran
 * pour une poignée de deux champs). `portrait-mathieu.png` reste le nom de FICHIER technique,
 * inchangé depuis A-145 §1 bis.
 */
const HOTE_MATT: Interlocuteur = { nom: "Matt", portrait: "/avatar/portrait-mathieu.png" };

/**
 * ⟦C21 · A-146 §2⟧ Sur mobile, une seule fenêtre est montée à la fois : quand le halo pulsé de
 * Matt vise un élément d'une fenêtre FERMÉE, c'est l'onglet lui-même qui pulse (repris de la
 * maquette F3, `.onglet.halo-pulse` sur « Chaîne » à l'étape ① du briefing) — pointer un bouton
 * qui n'est pas à l'écran ne servirait à rien. `null` : le halo vise un élément toujours visible
 * (l'horloge de l'en-tête) ou n'existe pas pour cette réplique.
 */
function ongletDuHalo(haloCible: string | null): OngletMobile {
  if (haloCible === null) return null;
  if (
    haloCible === "panneau-chaine" ||
    haloCible === "energie" ||
    haloCible === "sante-mentale" ||
    haloCible === "reputation" ||
    haloCible === "fidelite" ||
    haloCible === "momentum" ||
    haloCible === "fil" ||
    haloCible === "argent"
  ) {
    return "chaine";
  }
  if (
    haloCible === "publier" ||
    haloCible === "dormir" ||
    haloCible === "veille" ||
    haloCible === "arbre" ||
    haloCible === "longue"
  ) {
    return "actions";
  }
  return null;
}

/**
 * ⟦A-84 · SPEC-UI §3.7 pt 2 · PLAN §4.13⟧ **« Après la rue » — le bandeau permanent doré.**
 * « Le Tableau de bord reçoit un bandeau permanent doré : Victoire gravée — objectif : l'arbre
 * 100 % ({acquis}/39), paliers 2M/5M/10M célébrés. » Les paliers et l'exigence figée E5
 * existaient côté moteur ; l'accueil ne disait rien de la victoire gravée ni du compteur
 * d'arbre. Rendu UNIQUEMENT quand `flags.apres_la_rue` est posé (l'action
 * `continuer_apres_rue`) : une run ordinaire ne le voit jamais — l'état neutre de l'écran.
 *
 * Le compteur d'arbre lit le contenu CHARGÉ (les nœuds sous interrupteur n'y sont pas), pas
 * un « 39 » recopié : le jour où un interrupteur s'allume, le dénominateur suit tout seul.
 * ✎ Le « J{n} » de la victoire n'est pas affiché : l'état ne porte pas l'heure de la gravure
 * (seule la ligne `runs.victory_turn` côté serveur la connaît), et `types.ts` appartient à
 * une autre session — signalé dans l'avenant.
 */
function BandeauApresLaRue({ state }: { state: GameState }) {
  const noeuds = contenu().skills.length;
  const acquis = state.skills.length;
  const prochain = (PALIERS_POST_1M as readonly number[]).find(
    (p) => !(state.milestones_reached as readonly number[]).includes(p),
  );
  const complet = noeuds > 0 && acquis >= noeuds;
  return (
    <div className="bandeau-apres-rue" role="status">
      <span className="bandeau-apres-rue-etoile" aria-hidden="true">
        ★
      </span>
      <strong>Victoire gravée</strong>
      {/* ⟦C57 · lot 4⟧ Les deux chiffres qui comptent (l'arbre, le prochain palier) ; la phrase d'avant, mot pour mot, derrière 📰. */}
      <span>
        {" "}
        · arbre {nombre(acquis)}/{nombre(noeuds)}
        {prochain !== undefined ? ` · palier ${nombreCourt(prochain)}` : " · paliers faits"}
      </span>
      <LesDessous titre="Victoire gravée" variante="icone" className="bandeau-dessous">
        <DessousBloc>
          <p className="dessous-texte">
            {"Après la rue : objectif l'arbre 100 % ("}
            {nombre(acquis)}/{nombre(noeuds)})
            {complet ? " — la plaque dorée t'attend au mémorial" : ""}
            {prochain !== undefined ? ` · prochain palier ${nombreCourt(prochain)}` : " · tous les paliers célébrés"}
          </p>
        </DessousBloc>
      </LesDessous>
    </div>
  );
}

/**
 * ⟦C57 · lot 4 · A-174⟧ **« Les dessous » d'une leçon BLOQUANTE de Matt.** Matt dit maintenant des répliques
 * COURTES (une idée, 12 mots au plus, `repliques_courtes`) ; ce qu'il disait avant, mot pour mot (les
 * `repliques` longues, puis « À retenir » en entier), rouvre derrière ce bouton, posé dans la boîte.
 *
 * Seulement pour une leçon bloquante : la boîte ne se ferme alors qu'au ▶ ou à « Passer ». Une réplique non
 * bloquante (la bulle) se referme au premier clic n'importe où, un bouton y serait mangé avant d'ouvrir.
 * `stopPropagation` : la boîte avance au moindre clic, et un clic dans le dialogue (portail React) remonte
 * jusqu'à elle ; ni l'ouverture ni la fermeture des dessous ne doivent faire parler Matt de plus.
 */
function DessousDeMatt() {
  const courante = useDecouverteStore((s) => s.courante);
  if (!courante) return null;
  const longues = courante.repliques ?? courante.corps;
  return (
    <div
      className="matt-dessous"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <LesDessous titre={courante.titre} variante="texte">
        <DessousBloc titre="Matt, en entier">
          {longues.map((texte, i) => (
            <p className="dessous-texte" key={i}>
              {texte}
            </p>
          ))}
        </DessousBloc>
        {courante.aRetenir !== aRetenirCourtDe(courante) ? (
          <DessousBloc titre="À retenir">
            <p className="dessous-texte">{courante.aRetenir}</p>
          </DessousBloc>
        ) : null}
      </LesDessous>
    </div>
  );
}

/**
 * ⟦C26 bis · point 3 bis⟧ **La tenue minimale de la planche « au lit »/« au bureau ».**
 *
 * Constat du chantier (`c26-hover-1280-03-pendant-dormir.png`) : en bac à sable, `act({type:
 * "sleep"})` résout en quelques millisecondes, la capture prise juste après le clic sur
 * Dormir montre déjà le RÉVEIL (horloge avancée, notifications « Nuit 1 »), jamais la planche
 * « au lit » que ⟦A-150⟧ a peinte pour cet instant précis. Le moteur ne change pas (CLAUDE.md :
 * « aucun accès horloge côté client » ne s'applique pas ici, c'est l'inverse, une temporisation
 * D'AFFICHAGE, jamais de calcul) : le résultat est déjà connu dès que `act()` résout, on retarde
 * seulement le moment où `Studio.tsx` cesse de dire « je dors » à `<Chambre>`.
 *
 * Même principe pour le travail (« Publier un short ») : 0,8 s de moins, la tenue n'a pas besoin
 * d'être aussi longue (rien à lire, contrairement au réveil qui succède à une nuit entière) mais
 * doit tout de même laisser le temps de VOIR le personnage assis à son bureau avant que le
 * panneau ne s'ouvre par-dessus.
 *
 * `prefers-reduced-motion` : le chantier chiffre EXPLICITEMENT le cas du sommeil (0,6 s, un tiers
 * de 1,8 s) mais ne dit rien du travail. Décision d'implémentation, pas une consigne de
 * Mathieu : le même ratio (÷3) est repris plutôt qu'une valeur inventée sans repère, cohérent
 * avec le « même principe » du chantier.
 */
const DUREE_TENUE_SOMMEIL_MS = 1800;
const DUREE_TENUE_SOMMEIL_REDUITE_MS = 600;
const DUREE_TENUE_TRAVAIL_MS = 800;
const DUREE_TENUE_TRAVAIL_REDUITE_MS = Math.round(
  (DUREE_TENUE_TRAVAIL_MS * DUREE_TENUE_SOMMEIL_REDUITE_MS) / DUREE_TENUE_SOMMEIL_MS,
);

/** Attend qu'AU MOINS `dureeMs` se soit écoulé depuis `debut` (`performance.now()`) : un
 *  `setTimeout` simple si rien ne s'est encore passé (le cas du travail, aucune requête entre
 *  temps), ou seulement le reliquat si une attente réseau a déjà mangé une partie du délai (le
 *  cas du sommeil, `act({type:"sleep"})`) : jamais une attente qui s'additionnerait au lieu de
 *  se chevaucher. */
function attendreAuMoins(debut: number, dureeMs: number): Promise<void> {
  const restant = dureeMs - (performance.now() - debut);
  if (restant <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    window.setTimeout(resolve, restant);
  });
}

/** Scores neutres : sert uniquement à demander au moteur le coût d'un short (§2.2). */
const SCORES_VIDES: AtelierScores = {
  ecriture: 0,
  tournage: 0,
  montage: 0,
  miniature: null,
  promotion: null,
  delegated: [],
};

export function Studio() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);
  // ⟦C14 · A-133⟧ Le palier qui attend sa pop-up (`PopupsPalier`) est aussi le signal
  // « célébration » de la pose du héros — même instant, un autre habillage (`game/heros.ts`).
  const paliersEnAttente = useRunStore((s) => s.paliersEnAttente);
  // ⟦A-129 · C11⟧ Le HUD progressif : le réglage « Tableau de bord complet » court-circuite
  // tout (comme avant le chantier), et la mémoire de révélation est LA MÊME que celle du mode
  // découverte — même `localStorage`, même clé, clefée par run.
  const hudComplet = useUIStore((s) => s.settings.hudComplet);
  const vuesDecouverte = useDecouverteStore((s) => s.vues);
  // ⟦C62 · lot tableau · A-179⟧ « Détails » du tableau de bord : la préférence du joueur (mémorisée), et l'ancre
  // de l'info-bulle de leçon affichée en ce moment (Matt présente la fidélité, le momentum, la hype : l'élément
  // dont il parle doit être sous les yeux, voir `cibleDansLeDetail`).
  const detailsPref = useUIStore((s) => s.settings.detailsTableauOuverts);
  const ancreBulle = useDecouverteStore((s) => s.couranteBulle?.ancre ?? null);
  // ⟦C23 §2⟧ Le bouton « i » (`expliquerLaNuit`) est retiré de la fenêtre Actions : son texte
  // devient une réplique de Matt, demandée à la main au premier survol/focus du bouton Dormir
  // (`game/decouverte.ts`, leçon « dormir », jamais due automatiquement : `demanderBulle` porte
  // déjà la mémoire "vue" : un seul appel utile par run).
  const demanderIndiceDormir = useDecouverteStore((s) => s.demanderBulle);
  const naviguer = useNavigate();
  const emplacement = useLocation();
  // ⟦C14⟧ `pending` seul ne dit PAS « en train de dormir » : il vaut `true` pour n'importe
  // quelle action en vol (veille, commentaires…). Ce drapeau local ne porte que le sommeil —
  // posé juste avant `act({type:"sleep"})`, retiré dans le `finally`, jamais persisté.
  const [enSommeil, setEnSommeil] = useState(false);
  // ⟦C26 bis · point 3 bis⟧ Même principe que `enSommeil`, pour « Publier un short » : vrai entre
  // le clic et la navigation réelle vers `/nouvelle-video`, le temps de tenir la planche « au
  // bureau » (`ouvrirAtelier`, plus bas), jamais persisté, jamais lu ailleurs que dans ce
  // composant.
  const [enOuverture, setEnOuverture] = useState(false);
  // ⟦C32 · A-157⟧ **Le survol externe.** L'id de l'objet de la chambre que survole/focus en ce
  // moment un bouton de la fenêtre Actions — `null` : aucun bouton n'est survolé, la chambre
  // n'est pilotée que par ses propres hotspots (comportement d'avant ce chantier, inchangé).
  // Combiné dans `<Chambre>` avec son survol interne (`survolExterne`, `Chambre.tsx`) : un seul
  // mécanisme fait bouger l'avatar et basculer la planche d'état, que la souris/le clavier vise
  // le dessin ou le bouton qui porte le même nom.
  const [survolBouton, setSurvolBouton] = useState<string | null>(null);
  // ⟦C26 bis · point 3 bis⟧ Lu une seule fois ici (`Intro.tsx` le lit pareil, retapé, deux
  // écrans, une constante chacun, comme `HOTE_MATT`) : combien de temps tenir les deux planches
  // ci-dessous.
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  // ⟦C21 · A-146⟧ Repli des fenêtres « Ma chaîne »/« Journal »/« Actions » — mémorisé comme le
  // HUD progressif (`uiStore.settings`, même mécanisme que `hudComplet`).
  const chaineRepliee = useUIStore((s) => s.settings.fenetreChaineRepliee);
  const journalRepliee = useUIStore((s) => s.settings.fenetreJournalRepliee);
  const actionsRepliee = useUIStore((s) => s.settings.fenetreActionsRepliee);
  const setSetting = useUIStore((s) => s.setSetting);
  // ⟦C21 · A-146⟧ Bascule bureau (fenêtres en U) / mobile (onglets) — même seuil que la
  // maquette F3 (768 px), même patron que `BandeauPetitEcran` (`matchMedia` + `resize`, un
  // pilotage externe du viewport — l'automatisation de vérification — ne déclenche pas toujours
  // `change`).
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia(REQUETE_FENETRES_MOBILE);
    const maj = () => setMobile(mq.matches);
    maj();
    mq.addEventListener("change", maj);
    window.addEventListener("resize", maj);
    return () => {
      mq.removeEventListener("change", maj);
      window.removeEventListener("resize", maj);
    };
  }, []);
  // ⟦C21 §4⟧ Une seule fenêtre mobile ouverte à la fois ; « Actions » par défaut au jour 1.
  // Retaper l'onglet actif la replie : la valeur devient `null`, la chambre respire.
  const [ongletMobile, setOngletMobile] = useState<OngletMobile>("actions");
  // ⟦C21 · A-146 §2⟧ Ce que Matt dit maintenant, ou `null` — la chambre est calme (état 2 de la
  // maquette F3). Appelé inconditionnellement (règle des hooks), avant le `if (!run)` : le hook
  // gère lui-même une run absente.
  const matt = useMattChambre();
  // ⟦C60 bis · A-177⟧ « Passer » (et « Les dessous ») de la boîte de Matt ne doivent jamais être recouverts par une
  // zone cliquable de la chambre : voir `controlesMatt.ts`.
  useControlesDeMattAuDessus();
  // ⟦C27 · A-153⟧ L'accueil (`screens/Accueil/Accueil.tsx`) joue PAR-DESSUS cet écran, à la
  // création d'une chaîne : tant qu'il parle encore (`accueilActif && !accueilGlissement`), CET
  // écran ne rend RIEN (aucun en-tête, aucune fenêtre, aucun onglet dans le DOM, exactement ce
  // que demande le critère 1 du chantier, « DOM et capture »). Dès que le glissement commence,
  // l'écran rend son interface réelle en entier (l'accueil se réduit PAR-DESSUS, `Accueil.tsx`
  // mesure `.zone-scene`/`.scene-mobile-zone` ci-dessous à ce moment précis) avec une entrée en
  // cascade (`.interface-arrivee`, `app.css`), jamais rejouée ensuite (une reprise de partie ne
  // passe jamais par `accueilStore`, `actif` y reste tout le temps faux).
  const accueilActif = useAccueilStore((s) => s.actif);
  const accueilGlissement = useAccueilStore((s) => s.glissement);

  if (!run) return null;
  if (accueilActif && !accueilGlissement) return null;
  const state = run.state;
  // Capturé en local : TypeScript ne resserre pas `run` (possiblement `null`) à travers la
  // frontière d'une fonction imbriquée (`corpsActions` plus bas) — une simple constante évite
  // de répéter `run!.bacASable` avec un `!` qui masquerait une vraie régression un autre jour.
  const bacASable = run.bacASable;
  const c = contenu();
  // ⟦C60 · A-177⟧ Les interrupteurs du ménage, lus UNE fois : un système coupé n'a plus ni objet flottant
  // dans la chambre, ni bouton dans « Actions », ni chiffre à lire (loyer, charges). Rallumé, il revient.
  const sys = interrupteurs();
  const panneauOuvert = emplacement.pathname !== "/";
  // ⟦C21 · A-146 §2⟧ `halo(id)` : cet élément est-il celui dont Matt parle maintenant ? Sur
  // mobile, `ongletCibleHalo` dit en plus QUEL onglet pulser quand cet élément vit dans une
  // fenêtre actuellement fermée (`ongletDuHalo`, plus haut).
  const halo = (id: string) => matt?.haloCible === id;
  const ongletCibleHalo = ongletDuHalo(matt?.haloCible ?? null);
  // ⟦C14 · A-133⟧ La pose du héros — trois signaux déjà là, projetés en une pose (game/heros.ts).
  // ⟦C26 bis · point 3 bis⟧ `travail` inclut désormais `enOuverture` : la planche « au bureau »
  // se tient DÈS le clic sur « Publier un short », avant même que le panneau ne soit monté
  // (`emplacement.pathname` ne change qu'à la navigation, retardée exprès par `ouvrirAtelier`).
  const pose = poseHeros({
    celebration: paliersEnAttente.length > 0,
    sommeil: enSommeil,
    travail: emplacement.pathname === "/nouvelle-video" || enOuverture,
  });
  // ⟦C26 bis · point 3 bis⟧ Le résultat est déjà connu dès que `act()` résout ; `enSommeil` reste
  // vrai le temps de la tenue minimale (`attendreAuMoins`), jamais un calcul refait, une simple
  // temporisation d'affichage. `debut` est posé AVANT l'appel réseau : une nuit qui prend déjà
  // 1,8 s ou plus (lenteur réseau, mode compte) n'attend pas une seconde de plus.
  const dormir = async () => {
    setEnSommeil(true);
    const debut = performance.now();
    try {
      await act({ type: "sleep" });
    } finally {
      await attendreAuMoins(debut, reducedMotion ? DUREE_TENUE_SOMMEIL_REDUITE_MS : DUREE_TENUE_SOMMEIL_MS);
      setEnSommeil(false);
    }
  };
  // ⟦C26 bis · point 3 bis⟧ « Même principe pour le travail » : la planche « au bureau » se
  // tient 0,8 s avant que le panneau ne s'ouvre (`naviguer`, retardé jusqu'à la fin de l'attente,
  // jamais avant). Partagé par le bouton bureau de la chambre ET le bouton primaire de la
  // fenêtre Actions (`corpsActions`, plus bas), un seul chemin, jamais deux copies qui
  // pourraient un jour diverger sur la durée de la tenue.
  const ouvrirAtelier = async () => {
    setEnOuverture(true);
    const debut = performance.now();
    await attendreAuMoins(debut, reducedMotion ? DUREE_TENUE_TRAVAIL_REDUITE_MS : DUREE_TENUE_TRAVAIL_MS);
    setEnOuverture(false);
    naviguer("/nouvelle-video");
  };
  // ⟦C26 bis · point 3 bis⟧ Les actions restent désactivées pendant les DEUX tenues (le chantier
  // le demande explicitement pour le sommeil, « même principe » pour le travail), au-delà de la
  // seule fenêtre `pending` du moteur, qui retombe à `false` dès que `act()` résout, avant même
  // que la tenue minimale ne commence.
  const enTransition = enSommeil || enOuverture;

  // ⟦C32 · A-157⟧ **900 ms.** Sur mobile il n'y a pas de survol (⟦A-154⟧, la règle est déjà
  // posée pour la fenêtre Actions) : `onTouchStart` pose donc `survolBouton` lui-même, faute de
  // `onMouseEnter` pour l'annuler ensuite (`onTouchEnd`/`onMouseLeave` ne se déclenchent pas de
  // façon fiable après un tap). Un simple minuteur de repli referme la fenêtre le temps
  // approximatif d'une action lue à l'écran — jamais un blocage : l'action elle-même part par
  // le `onClick` normal du bouton, sur le MÊME tap, sans attendre ce délai.
  const DUREE_SURVOL_TOUCHER_MS = 900;
  /**
   * ⟦C32 · A-157⟧ Les gestionnaires de survol/focus/toucher d'un bouton d'action, pour l'id
   * d'objet de la chambre auquel il correspond (`Chambre.tsx`, `survolExterne`) — un seul jeu de
   * gestionnaires, posé sur chaque bouton qui a un objet peint dans la chambre, plutôt que huit
   * copies quasi identiques. `onEnter` optionnel : le bouton Dormir a SA PROPRE chose à faire au
   * survol (`demanderIndiceDormir`, plus bas) en plus de piloter l'avatar — les deux cohabitent
   * sans que l'un écrase l'autre.
   */
  const survolBoutonHandlers = (id: string, onEnter?: () => void) => ({
    onMouseEnter: () => {
      onEnter?.();
      setSurvolBouton(id);
    },
    onMouseLeave: () => setSurvolBouton((cur) => (cur === id ? null : cur)),
    onFocus: () => {
      onEnter?.();
      setSurvolBouton(id);
    },
    onBlur: () => setSurvolBouton((cur) => (cur === id ? null : cur)),
    onTouchStart: () => {
      setSurvolBouton(id);
      window.setTimeout(() => setSurvolBouton((cur) => (cur === id ? null : cur)), DUREE_SURVOL_TOUCHER_MS);
    },
  });

  const dejaRevele = new Set(vuesDecouverte);
  /** `true` si CET élément doit être rendu — état neutre : 3 jauges de base + rien d'autre. */
  // ⟦C60 bis · A-177⟧ La réputation a quitté l'écran (décision de Matt, 03/10/2026) : ni au premier
  // mouvement, ni par « Tableau de bord complet ». Rallumée dans `content/meta.json`, elle revient.
  const revele = (id: ElementHud) => hudOuvert(id, sys) && (hudComplet || hudVisible(id, state, dejaRevele, sys));

  // Le prochain palier non franchi, et la part du chemin qui y mène **depuis le précédent** :
  // une barre qui repart de zéro à chaque palier se remplit visiblement, là où une barre
  // absolue vers le million reste à 0 % pendant les trois quarts de la partie.
  const indexPalier = PALIERS.findIndex((p) => state.subscribers < p);
  const atteintLObjectif = indexPalier === -1;
  const prochainPalier = atteintLObjectif ? OBJECTIF : PALIERS[indexPalier]!;
  const palierPrecedent = indexPalier <= 0 ? 0 : PALIERS[indexPalier - 1]!;
  const partVersPalier = atteintLObjectif
    ? 1
    : Math.max(
        0,
        (state.subscribers - palierPrecedent) / Math.max(1, prochainPalier - palierPrecedent),
      );

  const cout = (action: Action) => libelleCout(actionCost(action, state));
  const coutShort = cout({
    type: "produce_video",
    format: "short",
    subject_id: "",
    scores: SCORES_VIDES,
  });
  // ⟦C62 · A-179 · friction 8⟧ Sans idée en main, « Publier » ne peut rien lancer (la fenêtre « Nouvelle vidéo » grise déjà
  // son bouton, mais le joueur y entrait pour l'apprendre) : le bouton se grise ICI, et dit pourquoi à la place de son
  // coût (« plus d'idée : fais une veille », ou « dors d'abord » tant que la veille n'est pas ouverte).
  const sansIdee = state.inspiration.length === 0;
  const legendePublier = sansIdee ? raisonSansIdee(ouvert(state, "veille")) : coutShort;

  // ── Les objets de la chambre. L'ordre de la liste = l'ordre de l'escalier A-5 : chaque
  //    déverrouillage AJOUTE une ligne ici, et la chambre se remplit sous les yeux du joueur.
  // ⟦Micro-correctif reskin 8-bit, C14 · A-134⟧ Bureau et lit sont déjà peints dans le fond
  // R1/R2 (`.chambre-scene`) : plus d'emoji à poser dessus (`emoji` absent ⇒ hotspot invisible,
  // voir Chambre.tsx), juste un contour au survol/focus (⟦C31⟧ : plus de label, la plaque nom +
  // coût a quitté la scène). Les coordonnées
  // viennent d'être recalées sur le dessin réel : x/y pointaient encore sur le repère de
  // l'ancien décor doodle (aucune image dessous), et tombaient sur le mauvais meuble — le
  // bureau atterrissait sur le matelas (à gauche), le lit sur le plancher du bureau (à droite) —
  // vérifié en surlignant les deux hotspots dans le navigateur. `taille` n'a plus d'effet sans
  // emoji ; laissé tel quel, il ne fait plus rien de mal.
  const objets: ObjetChambre[] = [
    {
      id: "bureau",
      // ⟦C31⟧ « Le bureau » ne s'affiche plus dans la scène (décision de Matt du 16/09) : `nom`
      // ne sert plus qu'au nom accessible (`aria-label`), le libellé court sous 768 px
      // (`nomCourt`, `.objet-nom-court`) qui le remplaçait n'a donc plus de raison d'exister,
      // retiré avec lui.
      nom: "Le bureau",
      cout: legendePublier,
      x: 63,
      y: 46,
      taille: 1.5,
      // ⟦C26 bis · point 3 bis⟧ `ouvrirAtelier` tient la planche « au bureau » 0,8 s avant la
      // navigation, plus un `naviguer` direct, même chemin que le bouton primaire des Actions.
      action: () => void ouvrirAtelier(),
      desactive: pending || enTransition || sansIdee,
    },
    {
      id: "lit",
      nom: "Le lit",
      cout: cout({ type: "sleep" }),
      x: 23,
      y: 62,
      taille: 1.4,
      action: () => void dormir(),
      desactive: pending || enTransition,
    },
  ];

  // ⟦C29 · lot A · point 2⟧ Comme le bureau et le lit (`Micro-correctif reskin 8-bit` plus haut) :
  // la fenêtre est déjà peinte dans le fond sur les cinq niveaux (`HOTSPOTS_PAR_SCENE`, `game/
  // chambre.ts`, colonne « fenêtre » cochée partout). Poser en plus l'ancien emoji 🪟 (rendu par
  // certains systèmes comme une icône pixel de fenêtre en bois) revenait à dessiner l'objet DEUX
  // fois, un reliquat de l'ancien décor doodle, exactement le défaut déjà corrigé pour bureau/
  // lit, jamais reporté ici. `emoji` absent ⇒ hotspot invisible (repli 🪟 posé dans
  // `REPLI_EMOJI["fenetre"]`, `game/chambre.ts`, pour la scène hypothétique qui ne la peindrait
  // pas) : le dessin porte l'objet, le hotspot n'a qu'un contour au survol/focus et son étiquette.
  if (ouvert(state, "veille")) {
    objets.push({
      id: "fenetre",
      nom: "La fenêtre",
      cout: cout({ type: "veille" }),
      x: 90,
      y: 44,
      taille: 1.3,
      action: () => void act({ type: "veille" }),
      desactive: pending,
    });
  }

  // ⟦C33 · A-158, décision de Matt du 17/09⟧ La pastille 📱 quitte la chambre : posée à une
  // position inventée (x:31, y:66, sur le lit — aucun téléphone dessiné sur cette planche,
  // ⟦A-137⟧) elle n'avait pas de hotspot dédié dans `HOTSPOTS_PAR_SCENE`, et l'agrandissement de
  // la zone du lit (⟦C30⟧, `empriseObjetChambre`) lui a fait voler son clic et son survol.
  // Le bouton « Lire les commentaires » du bas reste le seul accès (`act({ type:
  // "lire_commentaires" })`, plus bas) ; son survol continue de montrer le personnage au
  // téléphone via `OBJET_PAR_BOUTON_ACTION.commentaires` (⟦A-157⟧, `survolBoutonHandlers`) —
  // cette table et la planche d'état `telephone` (`game/chambre.ts`, `PoseEtat`) sont
  // volontairement inchangées, elles ne dépendent pas de cet objet de scène.

  // ⟦C33 · A-158, décision de Matt du 17/09 · C34 · A-159⟧ **Le panneau à post-it dessiné EST le
  // panneau de liège de l'arbre de compétences.** Deux états, désormais un CALQUE posé sur le
  // fond (⟦C34⟧, `scripts/chambre-calques-c34.py`) plutôt qu'un fichier entier à choisir : vide
  // (liège nu, le fond lui-même) tant que l'arbre n'est pas débloqué, rempli (post-it) une fois
  // qu'on peut le consulter — c'est `Chambre.tsx` qui pose le calque selon `ouvert(state,
  // "arbre")` (`calqueLiegeChambreN1`), cet objet ne porte que le HOTSPOT. Gate donc sur
  // `"arbre"`, plus `"tendances"` : avant ce déblocage, l'objet
  // n'existe pas du tout (⟦A-5⟧ « ce qui n'est pas déverrouillé n'existe pas ») — ni cliquable
  // ni survolable, la pièce montre alors le liège vide sans qu'on puisse le viser. L'ancien
  // objet séparé `liege` (pastille 🌳 à une position inventée, jamais un hotspot dédié dans
  // `HOTSPOTS_PAR_SCENE`) disparaît avec lui : c'est ce même objet `tableau`, déjà peint dans le
  // fond (⟦C29 · lot A · point 2⟧, `emoji` absent ⇒ hotspot invisible), qui ouvre l'arbre.
  if (ouvert(state, "arbre")) {
    objets.push({
      id: "tableau",
      nom: "Le panneau de liège",
      cout: `${points(state.pc)} à placer`,
      x: 27,
      y: 35,
      taille: 1.1,
      action: () => naviguer("/arbre"),
    });
  }

  // ⟦C33 · A-158⟧ **Le tableau des tendances**, peint au mur à la place des deux anciens
  // cadres-photo (à côté du panneau de liège) — remplace la liste de texte flottante qui vivait
  // ici (`detail`, ⟦A-7 décision 6⟧ : elle ÉTAIT l'info en place, désormais c'est le clic qui
  // l'ouvre). Au survol, « le personnage va devant le mur » : il n'a pas de planche à lui, il
  // réutilise celle du panneau de liège voisin (`poseEtatDeObjet`, `game/chambre.ts` — assumé et
  // signalé à Matt). Au clic, les trois tendances s'ouvrent dans `AppDialog` (`dialog.info`),
  // jamais `window.alert` — exactement l'information qu'affichait la liste flottante : la phase
  // (🔥 pic, 🌡 montante) et le nom lisible.
  if (ouvert(state, "tendances")) {
    const lignesTendances = state.trends
      .filter((t) => t.trend_id !== "")
      .map(
        (t) =>
          `${t.phase === "pic" ? "🔥" : "🌡"} ${c.parTendance.get(t.trend_id)?.nom ?? t.trend_id}` +
          (state.skills.includes("analytics_addict") ? ` (${t.phase})` : ""),
      );
    objets.push({
      id: "tendances",
      nom: "Le tableau des tendances",
      x: 39,
      y: 29,
      taille: 1,
      action: () =>
        void dialog.info({
          title: "Les tendances",
          body: lignesTendances.length > 0 ? lignesTendances.join("\n") : "Aucune tendance en ce moment.",
          icone: "📈",
        }),
    });
  }

  // ⟦C60 · A-177⟧ L'étagère (la gestion de la bibliothèque) est coupée : aucun objet dans la pièce. La vidéo
  // longue reste ouverte par le bouton Nouvelle vidéo.
  if (sys.bibliotheque && ouvert(state, "longue")) {
    const actives = evergreensActives(state);
    const rente = actives.reduce((total, v) => total + renteHebdoVues(v, state.clock_h), 0);
    objets.push({
      id: "etagere",
      emoji: "📚",
      nom: "L'étagère",
      cout: `${state.videos.length} vidéo(s)`,
      // Reculée du coin (88 → 77) à l'époque où le coin haut-droit du mur appartenait à la rangée de
      // plaques (`.plaques-mur`). ⟦C55 bis · A-175⟧ Cette rangée n'existe plus (les plaques sont au tableau
      // dessiné de `chambre-n1`) ; la position, elle, n'a pas bougé : un objet d'action ne se déplace pas
      // pour un décor.
      x: 77,
      y: 14,
      taille: 1.2,
      action: () => naviguer("/bibliotheque"),
      detail:
        actives.length > 0 ? (
          <span className="objet-liste">
            <span>
              🌲 {actives.length}/8 · ~{nombre(rente)} vues/sem.
            </span>
          </span>
        ) : undefined,
    });
  }

  // ⟦C60 · A-177⟧ Ni la boîte à Clicks (le logement et son loyer) ni le carton (la boutique) : coupés, ils ne
  // flottent plus dans la pièce. Le compteur de Clicks, lui, reste dans « Ma chaîne » : il paie le Marché et l'Équipe.
  if (sys.logement && ouvert(state, "argent")) {
    // ⟦A-11⟧ La boîte à Clicks cesse d'être un compteur décoratif : c'est par elle qu'on va
    // voir ce que la vie coûte — et, si ça tourne mal, qu'on remonte les cartons.
    objets.push({
      id: "boite",
      emoji: "🏦",
      nom: "La boîte à Clicks",
      cout: `${nombre(state.money)} ₵`,
      x: 74,
      y: 40,
      taille: 0.9,
      action: () => naviguer("/logement"),
      detail: (
        <span className="objet-liste">
          <span>
            {state.flags.chez_les_parents === true
              ? "chez les parents · 0 ₵/jour"
              : `loyer ${nombre(chargesDuJour(state, sys))} ₵/jour`}
          </span>
        </span>
      ),
    });
  }
  if (sys.boutique && ouvert(state, "argent")) {
    // ⟦A-11 §4⟧ « La collab fait entrer l'argent, la boutique le fait sortir. » Le carton est
    // l'objet de la boutique dans la chambre : ce qu'on achète ici, on le voit arriver.
    objets.push({
      id: "colis",
      emoji: "📦",
      nom: "Le carton de livraison",
      cout: "la boutique",
      x: 17,
      y: 72,
      taille: 1,
      action: () => naviguer("/boutique"),
    });
  }

  // ── ⟦C62 · lot tableau · A-179⟧ **Le tableau de bord limpide.** Matt : *« Il faut que le jeu soit simple et
  //    limpide. »* Audit du 21/10/2026, friction n° 1 : 5 lignes de chiffres au départ, 14 au million, six jauges
  //    sans mot (le nom ne vivait que dans l'infobulle, absente au toucher). Réglage de contenu
  //    (`meta.tableau_limpide`, défaut éteint = le tableau d'avant, au pixel près) : quand il est allumé,
  //    - chaque jauge écrit son NOM à côté de son icône (`nomVisible`) ;
  //    - à plat ne restent que ce qui peut tuer ou faire gagner : abonnés, énergie, santé mentale, Clicks (dès le
  //      Grand Saut, le HUD progressif ci-dessus en décide) ;
  //    - tout le reste (fidélité, hype, momentum, niveau, ratio, surcharge, rivaux…) se range dans « Détails »,
  //      fermé par défaut, et ABSENT tant qu'il n'y a rien à y ranger. « Tableau de bord complet » (Réglages)
  //      garde son sens d'avant : tout à plat, d'emblée.
  const limpide = tableauLimpide();
  const rangerLeDetail = limpide && !hudComplet;

  const jaugeReputation = revele("reputation") ? (
    <Jauge
      icone="⭐"
      nom="Réputation"
      valeur={state.reputation}
      teinte="--gauge-rep"
      suivi="reputation"
      ancre="reputation"
      halo={halo("reputation")}
      nomVisible={limpide}
      titre="Ce que le milieu pense de toi : elle ouvre des portes (collabs, sponsors) et amortit les mauvaises tuiles. Un bad buzz la fait chuter vite ; elle ne remonte que lentement."
    />
  ) : null;
  const jaugeFidelite = revele("fidelite") ? (
    <Jauge
      icone="🤝"
      nom="Fidélité"
      valeur={state.loyalty}
      teinte="--gauge-fidelite"
      suivi="fidelite"
      ancre="fidelite"
      halo={halo("fidelite")}
      nomVisible={limpide}
      titre={
        sys.alignement
          ? "La part de ton public qui te suit vraiment, pas juste un chiffre d'abonnés. Elle monte quand tu lis tes commentaires et tiens ta ligne, elle descend quand tu disparais."
          : "La part de ton public qui te suit vraiment, pas juste un chiffre d'abonnés. Elle monte quand tu lis tes commentaires, elle descend quand tu disparais."
      }
    />
  ) : null;
  // ⟦SPEC-FIL §9.4 · PLAN §4.14 B⟧ **La 8ᵉ jauge.** Placée juste après la Fidélité, et pas au bout de la rangée :
  // ce sont les deux jauges de communauté, et elles se lisent ensemble — la Fidélité, ce sont les gens qui
  // restent ; la Hype, c'est leur température aujourd'hui. `revele("hype")` reprend le même déclencheur qu'avant
  // (`flags.fil_decouvert`) via `game/hud.ts` : ⟦A-5⟧ « ce qui n'est pas déverrouillé est absent, pas grisé »
  // reste vrai au caractère près.
  const jaugeHype = revele("hype") ? (
    <Jauge
      icone="💬"
      nom="Hype"
      valeur={state.hype ?? 0}
      teinte="--gauge-fidelite"
      suivi="hype"
      ancre="fil"
      halo={halo("fil")}
      nomVisible={limpide}
      titre="L'énergie de ta communauté. Elle pousse tes vues, et elle retombe si tu disparais."
    />
  ) : null;
  const jaugeMomentum = revele("momentum") ? (
    <Jauge
      icone="🚀"
      nom="Momentum"
      valeur={state.momentum}
      min={0.8}
      max={1.5}
      halo={halo("momentum")}
      teinte="--gauge-momentum"
      affichage={`×${state.momentum.toFixed(2).replace(".", ",")}`}
      ancre="momentum"
      nomVisible={limpide}
      titre="Un multiplicateur, pas une jauge sur 100 : il pousse ou freine les vues de tout ce que tu publies. Publier régulièrement le fait monter, le silence le fait retomber."
    />
  ) : null;
  // ⟦A-1 point 8⟧ demandait cette jauge grisée + tooltip AVANT le Grand Saut, en permanence. ⟦A-129 · C11⟧ la fait
  // rejoindre le HUD progressif : à la création d'une partie elle n'existe pas du tout, comme les cinq autres —
  // l'écart avec A-1 point 8 est assumé et signalé dans le rapport de C11, pas tranché ici en silence. Dès
  // qu'elle apparaît (le Grand Saut), elle apparaît directe et chiffrée : plus rien à griser, puisqu'il n'y a
  // plus de « avant » à montrer.
  const jaugeClicks = revele("argent") ? (
    <Jauge
      icone="₵"
      nom="Clicks"
      valeur={state.money}
      min={0}
      max={Math.max(100, state.money)}
      teinte="--gauge-momentum"
      affichage={nombre(state.money)}
      ancre="argent"
      halo={halo("argent")}
      nomVisible={limpide}
      titre={
        sys.charges
          ? "Tes Clicks. Les charges tombent chaque jour."
          : // ⟦C60 bis · A-177⟧ Les Clicks ne paient plus que le Marché et l'Équipe (l'Arbre se paie en points).
            "Tes Clicks. Tes vues en rapportent : ils paient le Marché et l'Équipe."
      }
    />
  ) : null;

  // Les lignes que « Détails » rangerait à cet instant : la MÊME présence que celle des composants qui s'affichent
  // (le HUD progressif pour les jauges, l'escalier, la partie). Rien à ranger ⇒ pas de bouton.
  const lignesDetail = lignesDuDetail({
    surcharge: surchargeAccordee(state) > 0,
    reputation: jaugeReputation !== null,
    fidelite: jaugeFidelite !== null,
    hype: jaugeHype !== null,
    momentum: jaugeMomentum !== null,
    ma_ligne: ouvert(state, "image"),
    niveau: revele("niveau"),
    ratio: marche(state) !== null,
    niche: nicheReconnue(state) !== null,
    // Même garde que `Concurrence` (`PressionsDuTemps.tsx`) : personne à comparer avant le premier palier.
    rivaux: (hudComplet || state.subscribers >= (PALIERS[0] ?? 0)) && rivauxLisibles(state, c).length > 0,
  });
  const aDesJaugesRangees = lignesDetail.some((id) => id === "reputation" || id === "fidelite" || id === "hype" || id === "momentum");
  // Ouvert si le joueur l'a ouvert, OU le temps qu'une leçon de Matt montre du doigt une ligne qui y est rangée
  // (l'info-bulle ou le halo de sa réplique) : elle ne doit jamais viser une porte fermée.
  const detailsOuverts = detailsPref || cibleDansLeDetail(ancreBulle) || cibleDansLeDetail(matt?.haloCible);

  // ── ⟦C21 · A-146⟧ Le corps de la fenêtre « Ma chaîne » : compteur + barre de palier (+
  //    LaMarche/BandeauNiche, déjà nichés dedans), les jauges du HUD progressif (inchangé), puis
  //    les bandeaux d'état (après-la-rue, alertes, santé mentale) et la concurrence. Une seule
  //    fois calculé, posé tel quel dans la fenêtre du bureau ET dans le panneau mobile (jamais
  //    les deux montés en même temps, cf. `mobile` plus haut).
  const corpsChaine = (
    <>
      {state.flags.apres_la_rue ? <BandeauApresLaRue state={state} /> : null}
      <AlertesChaine state={state} />
      {/* ⟦C60 · A-177⟧ Souffler garde un objet de la chambre, comme les autres boutons : le lit (un repos). */}
      <BandeauSM
        state={state}
        onSouffler={() => naviguer("/souffler")}
        survol={survolBoutonHandlers(OBJET_PAR_BOUTON_ACTION.souffler)}
      />
      {/* Le compteur d'abonnés est le SCORE du jeu : il était noyé au milieu des six
          autres jauges, à la même taille qu'elles (remarque de Mathieu). Il devient
          l'ancre visuelle du HUD.

          Et la barre vise le **prochain palier**, pas le million : à 0 abonné,
          « 0 / 1 000 000 » ne montre aucun progrès et ne motive personne, alors que
          « 0 sur 100 » se remplit dès la première vidéo. L'objectif final reste écrit,
          en petit — c'est l'horizon, pas la jauge. */}
      <div className={`hud-abonnes${limpide ? " hud-abonnes-limpide" : ""}${halo("panneau-chaine") ? " halo-pulse" : ""}`}>
        <strong className="compteur">{nombre(state.subscribers)}</strong>
        <span className="hud-abonnes-mot">abonnés</span>
        <div
          className="hud-palier-barre"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(partVersPalier * 100)}
          aria-label={`Progression vers ${nombreCourt(prochainPalier)} abonnés`}
        >
          <i style={{ width: `${Math.min(100, partVersPalier * 100)}%` }} />
        </div>
        {/* ⟦C21 quater · point 3⟧ Deux formulations, comme `.entete-nom-long`/`-court` : la
            colonne « Ma chaîne » redescend à 280 px sur ce palier (768-1599 px, pour laisser un
            peu plus de largeur à la scène et à la fenêtre Actions), trop étroite pour le texte
            complet (« prochain palier 100 · objectif 1,00 M » mesure 267,6 px à 16 px, contre
            256 px de contenu disponible à 280 px de colonne). `legende-courte` retire
            « prochain » et arrondit l'objectif à l'entier (`OBJECTIF` vaut TOUJOURS 1 000 000,
            « 1 M » est donc une valeur en dur assumée, pas un `nombreCourt` généralisé) : les
            deux versions restent dans le DOM, `app.css` choisit laquelle affiche selon la
            largeur d'écran, jamais du texte recalculé en JS. */}
        <span className="hud-palier-legende">
          {atteintLObjectif ? (
            <>
              <span className="legende-longue">objectif atteint · {nombreCourt(OBJECTIF)}</span>
              <span className="legende-courte">objectif atteint · 1 M</span>
            </>
          ) : (
            <>
              <span className="legende-longue">
                prochain palier <strong>{nombreCourt(prochainPalier)}</strong> · objectif{" "}
                {nombreCourt(OBJECTIF)}
              </span>
              <span className="legende-courte">
                palier <strong>{nombreCourt(prochainPalier)}</strong> · objectif 1 M
              </span>
            </>
          )}
        </span>
        {rangerLeDetail ? null : (
          <>
            <LaMarche state={state} />
            <BandeauNiche state={state} />
          </>
        )}
      </div>
      {/* ⟦A-129 · C11⟧ HUD progressif : à la création d'une partie, seules abonnés + énergie
          + santé mentale sont visibles — les deux dernières ici. Chaque autre jauge
          apparaît la première fois qu'elle bouge (`revele`, `game/hud.ts`), et le reste
          persiste pour la run une fois apparu.
          ⟦C62 · lot tableau⟧ Tableau limpide : ici, à plat, les trois (quatre dès le Grand Saut) jauges NOMMÉES ;
          les jauges secondaires et tout le reste vivent dans « Détails » juste dessous. */}
      <div className="jauges">
        <JaugeEnergie
          state={state}
          halo={halo("energie")}
          nomVisible={limpide}
          avecSurcharge={!rangerLeDetail}
        />
        <Jauge
          icone="❤"
          nom="Santé mentale"
          valeur={state.mental}
          teinte="--gauge-sm"
          suivi="sm"
          halo={halo("sante-mentale")}
          nomVisible={limpide}
          titre="Ta santé mentale : la colonne vertébrale du risque. Elle descend quand tu encaisses des coups durs ou que tu ne dors pas ; à zéro, c'est la fin de la partie. Dormir la remonte — contrairement à l'énergie (⚡), elle prend son temps."
        />
        {rangerLeDetail ? null : (
          <>
            {jaugeReputation}
            {jaugeFidelite}
            {jaugeHype}
            {jaugeMomentum}
          </>
        )}
        {jaugeClicks}
      </div>
      {rangerLeDetail ? (
        lignesDetail.length > 0 ? (
          <section className="hud-details">
            <button
              type="button"
              className="hud-details-bouton"
              aria-expanded={detailsOuverts}
              aria-controls="hud-details-corps"
              onClick={() => setSetting("detailsTableauOuverts", !detailsPref)}
            >
              <span>Détails</span>
              <span aria-hidden="true">{detailsOuverts ? "▾" : "▸"}</span>
            </button>
            {detailsOuverts ? (
              <div id="hud-details-corps" className="hud-details-corps">
                {lignesDetail.includes("surcharge") ? <LigneSurcharge state={state} /> : null}
                {aDesJaugesRangees ? (
                  <div className="jauges">
                    {jaugeReputation}
                    {jaugeFidelite}
                    {jaugeHype}
                    {jaugeMomentum}
                  </div>
                ) : null}
                {/* ⟦A-5 étage 3 · A-62 · C40 · A-163⟧ « Ma ligne » : toujours lisible sans clic (⟦A-7 décision
                    6⟧), `null` tant que l'image de chaîne n'est pas débloquée (`IndicateurMaLigne`, `Chambre.tsx`). */}
                <IndicateurMaLigne state={state} />
                {revele("niveau") ? (
                  <p className="hud-progression">
                    Niveau {state.level} · {state.counters.videos_published} vidéo(s) ·{" "}
                    {ouvert(state, "arbre") ? points(state.pc) : `${nombre(state.xp)} XP`}
                  </p>
                ) : null}
                <div className="hud-details-lignes">
                  <LaMarche state={state} />
                  <BandeauNiche state={state} />
                </div>
                <Concurrence state={state} />
              </div>
            ) : null}
          </section>
        ) : null
      ) : (
        <>
          {/* ⟦A-5 étage 3 · A-62 · C40 · A-163⟧ « Ma ligne », juste après les jauges — sortie du
              milieu de la scène (retour de Matt, 18/09 : « ça va pas, cette grosse fenêtre au
              milieu »). Toujours lisible sans clic (⟦A-7 décision 6⟧), `null` tant que l'image de
              chaîne n'est pas débloquée (voir `IndicateurMaLigne`, `Chambre.tsx`). */}
          <IndicateurMaLigne state={state} />
          {revele("niveau") ? (
            <p className="hud-progression">
              Niveau {state.level} · {state.counters.videos_published} vidéo(s) ·{" "}
              {ouvert(state, "arbre") ? points(state.pc) : `${nombre(state.xp)} XP`}
            </p>
          ) : null}
          {/* ⟦A-15 §3⟧ Juste sous TON compteur d'abonnés : c'est le seul endroit où la
              comparaison se fait sans y penser. Rien à cliquer, rien à subir — on regarde. */}
          <Concurrence state={state} />
        </>
      )}
    </>
  );

  // ── ⟦C21 · A-146⟧ Le corps de la fenêtre « Journal » : la tendance du jour en tête (carte
  //    rouge), puis les offres à fenêtre ⟦A-15⟧, puis les 10 dernières notifications ⟦SPEC-UI
  //    §3.1⟧. Le bouton Journal de l'en-tête ouvre toujours le journal complet.
  const corpsJournal = (
    <>
      {/* ⟦A-15⟧ Ce qui est ouvert **maintenant** et se refermera sans toi : les offres à fenêtre
          et la vague chaude qu'aucune de tes idées ne porte. Le récapitulatif ⟦A-16⟧ raconte ces
          mêmes faits, mais après coup — ici, c'est la moitié « prévenir » de l'avenant. Neutre
          par défaut : sans offre ouverte ni vague ratée, le composant ne rend rien.
          ⟦C21 · A-146⟧ C'est ELLE qui tient la place de « la tendance du jour, carte rouge » de
          la maquette F3 (`.pression-vague` prend le rouge d'alerte ci-dessous, `app.css`) : un
          second bloc qui aurait REDIT la même chaude (`vaguesRatees`, déjà lu ici) avec un texte
          retapé à la main aurait pu finir par se contredire — une seule vérité, un seul endroit. */}
      <FenetresOuvertes state={state} />
      {/* ⟦SPEC-UI §3.1⟧ « NOTIFICATIONS (10 dernières) [Journal ▸] » — le croquis, enfin tenu.
          L'accueil n'en montrait que 3 : dormir deux nuits en produisait davantage, et le
          joueur devait ouvrir le Journal pour lire ce qui venait de lui arriver. Les 10
          dernières sont là, la plus récente en tête ; la hauteur est bornée en CSS (la fenêtre
          défile elle-même, jamais la page) et le reste se fait défiler sur place. Le lien
          « Journal ▸ » est celui du croquis : l'historique complet est à un clic du fil. */}
      <p className="hud-notifs-tete">
        <span>📰 Notifications</span>
        <button className="hud-notifs-lien" type="button" onClick={() => naviguer("/journal")}>
          Journal ▸
        </button>
      </p>
      <ul className="hud-notifs">
        {run.journal.slice(-10).reverse().map((entree) => (
          <li className={`ton-${entree.ton}`} key={entree.id}>
            {/* ⟦C57 · lot 4⟧ Quand le fait a un détail (palier, jours écoulés…), la ligne reste courte et le détail
                s'ouvre derrière « Les dessous ». ⟦C62 · A-179⟧ Au plus UN « ? » par carte : ici, la ligne elle-même
                s'ouvre (un « ▸ » au bout), au lieu d'un « ? » par ligne (jusqu'à dix). La page Journal garde le sien. */}
            {entree.detail ? (
              <LigneAvecDetail detail={entree.detail} className="hud-notif-texte">
                <span aria-hidden="true">{entree.icone}</span> {entree.texte}
              </LigneAvecDetail>
            ) : (
              <span className="hud-notif-texte">
                <span aria-hidden="true">{entree.icone}</span> {entree.texte}
              </span>
            )}
          </li>
        ))}
      </ul>
    </>
  );

  /**
   * ⟦C21 · A-146⟧ Le corps de la fenêtre « Actions » : les boutons de l'ex-`.barre-actions`
   * (`Studio.tsx` avant ce chantier), en boutons deux tons (`.bouton`/`.bouton.primaire`,
   * système déjà en place — pas de nouvelle famille de bouton). `mobile` change seulement
   * l'habillage (une colonne pleine largeur au lieu d'une rangée qui passe en 2 lignes) : les
   * boutons et leurs conditions d'apparition sont rigoureusement les mêmes.
   */
  function corpsActions(mobile: boolean) {
    return (
      <>
        {/* ⟦C21 · A-146 §2.1⟧ Pendant une leçon bloquante, les actions se grisent — un
            `<fieldset disabled>` natif désactive tous les boutons qu'il contient d'un coup
            (jamais de voile : la chambre reste visible). `aria-disabled` suit déjà tout seul
            (un vrai `disabled` l'annonce déjà à un lecteur d'écran), pas la peine de le doubler. */}
        <fieldset
          className={`actions-liste${mobile ? " actions-liste-mobile" : ""}`}
          // ⟦C26 bis · point 3 bis⟧ « Les actions restant désactivées pendant ce temps », au-delà
          // du seul bouton Dormir/Publier, TOUTE la fenêtre Actions, le temps de la tenue
          // minimale (`enTransition`) : `matt?.bloquante` la désactivait déjà pendant une leçon,
          // même mécanisme, une cause de plus.
          disabled={matt?.bloquante || enTransition}
        >
          {/* ⟦C60 · A-177⟧ La leçon « la vidéo longue » visait le bouton Bibliothèque (`longue`) : la gestion de la
              bibliothèque est coupée, c'est ce bouton-ci qui porte désormais l'ancre et le halo (« Nouvelle vidéo »
              n'apparaît qu'une fois la longue ouverte). Sans ça, la leçon pointerait dans le vide. */}
          <button
            className={`bouton primaire${halo("publier") || (!sys.bibliotheque && halo("longue")) ? " halo-pulse" : ""}`}
            type="button"
            data-decouverte-ancre={!sys.bibliotheque && ouvert(state, "longue") ? "longue" : undefined}
            disabled={pending || enTransition || sansIdee}
            onClick={() => void ouvrirAtelier()}
            {...survolBoutonHandlers(OBJET_PAR_BOUTON_ACTION.publier)}
          >
            🎬 {ouvert(state, "longue") ? "Nouvelle vidéo" : "Publier un short"}
            <span className="cout">{legendePublier}</span>
          </button>

          {/* ⟦C23 §2⟧ Retour de Matt : « ça n'a rien à faire dans le menu du bas, passe-les
              dans les paramètres. » Le bouton « i » (`expliquerLaNuit`) a disparu : au premier
              survol/focus, Matt annonce la même chose en réplique (`game/decouverte.ts`, leçon
              « dormir »), une seule fois par run : `demanderBulle` porte déjà la mémoire "vue".
              Gardé par `!matt?.bloquante`, comme le `disabled` du fieldset : jamais demandé
              par-dessus une vraie leçon bloquante. */}
          <button
            className={`bouton${halo("dormir") ? " halo-pulse" : ""}`}
            type="button"
            disabled={pending || enTransition}
            onClick={() => void dormir()}
            // ⟦C27 quater · défaut n°3⟧ `!accueilActif` en plus : tant que l'accueil tient
            // l'écran (répliques ET glissement, `accueilStore.actif` ne repasse à faux qu'au
            // tout dernier instant, `App.tsx`), ce survol/focus ne doit RIEN demander — sans ce
            // garde, un curseur resté immobile là où « Passer » venait d'être cliqué (le geste
            // de test du chantier, mais un doigt qui traîne ferait pareil) se retrouve sous le
            // bouton Dormir dès que la fenêtre Actions glisse en place PAR-DESSUS lui, et le
            // navigateur y relit un survol SANS aucun mouvement de souris : la bulle « dormir »
            // partait alors PENDANT le glissement, remplaçant net la fenêtre Actions à peine
            // arrivée par la boîte de Matt (constat du chantier, `mesures-reel-run1.json`
            // instrumenté). `useMattChambre` tient déjà `!accueilActif` pour tout ce qu'il
            // demande TOUT SEUL (`libreBloquante`) ; ce hook-ci contourne ce garde puisqu'il
            // appelle `demanderBulle` directement, à la main — il lui fallait le même.
            {...survolBoutonHandlers(OBJET_PAR_BOUTON_ACTION.dormir, () => {
              if (!matt?.bloquante && !accueilActif) demanderIndiceDormir("dormir");
            })}
          >
            😴 Dormir
            <span className="cout">{cout({ type: "sleep" })}</span>
          </button>

          {/* ⟦C62 · lot 4⟧ « Laisser tourner » : l'outil de la fin de partie, dès que le producteur est en poste. */}
          <BoutonLaisserTourner />

          {ouvert(state, "veille") ? (
            <button
              className={`bouton${halo("veille") ? " halo-pulse" : ""}`}
              type="button"
              data-decouverte-ancre="veille"
              disabled={pending}
              onClick={() => void act({ type: "veille" })}
              {...survolBoutonHandlers(OBJET_PAR_BOUTON_ACTION.veille)}
            >
              🔭 Veille
              <span className="cout">{cout({ type: "veille" })}</span>
            </button>
          ) : null}

          {/* ⟦SPEC-FIL §9.3 · M3.23⟧ Le bouton OUVRE le Fil au lieu de jouer l'action à l'aveugle.
              Il la jouait directement : le joueur payait sa santé mentale sans jamais voir un seul
              commentaire, et les cinq réponses de PLAN §4.14 n'avaient aucune porte. Tant qu'aucun
              commentaire n'est arrivé, l'écran dit lui-même pourquoi il est vide — on ne cache pas
              la porte, on ne la verrouille pas non plus.

              Le compteur sur le bouton est le **nombre de commentaires en attente** : c'est la
              seule information qui décide d'y aller ou non, et elle rend le débordement du Fil
              (§4.5, le malus d'abandon) visible d'un coup d'œil, depuis la chambre. */}
          {ouvert(state, "commentaires") ? (
            <button
              className="bouton"
              type="button"
              onClick={() => naviguer("/fil")}
              {...survolBoutonHandlers(OBJET_PAR_BOUTON_ACTION.commentaires)}
            >
              💬 Lire les commentaires
              <span className="cout">
                {(state.fil?.length ?? 0) > 0
                  ? `${state.fil!.length} en attente`
                  : cout({ type: "lire_commentaires" })}
              </span>
            </button>
          ) : null}

          {ouvert(state, "arbre") ? (
            <button
              className={`bouton${halo("arbre") ? " halo-pulse" : ""}`}
              type="button"
              data-decouverte-ancre="arbre"
              onClick={() => naviguer("/arbre")}
              {...survolBoutonHandlers(OBJET_PAR_BOUTON_ACTION.arbre)}
            >
              🌳 Arbre
              <span className="cout">{points(state.pc)}</span>
            </button>
          ) : null}

          {/* ⟦C60 · A-177⟧ Bibliothèque, Boutique, Logement, Jalons : quatre systèmes coupés, quatre boutons qui
              n'existent plus (⟦A-5⟧ : absent, jamais grisé). Le code reste, derrière l'interrupteur. */}
          {sys.bibliotheque && ouvert(state, "longue") ? (
            <button
              className={`bouton${halo("longue") ? " halo-pulse" : ""}`}
              type="button"
              data-decouverte-ancre="longue"
              onClick={() => naviguer("/bibliotheque")}
            >
              📚 Bibliothèque
              <span className="cout">{state.videos.length} vidéo(s)</span>
            </button>
          ) : null}

          {/* ⟦A-11 §4⟧ Deux boutons, deux sens de l'argent : la boutique le fait sortir, le
              logement dit ce qu'il coûte à ne rien faire. Permanents comme les autres — ⟦A-7
              décision 3⟧ interdit d'obliger à chasser un objet du décor pour agir. */}
          {ouvert(state, "argent") ? (
            <>
              {/* ⟦C14 · A-133⟧ Seul bouton or de la barre — l'argent qui SORT, comme sur R4. */}
              {sys.boutique ? (
                <button className="bouton bouton-or" type="button" onClick={() => naviguer("/boutique")}>
                  🛒 Boutique
                  <span className="cout">{nombre(state.money)} ₵</span>
                </button>
              ) : null}
              {sys.logement ? (
                <button className="bouton" type="button" onClick={() => naviguer("/logement")}>
                  🏠 Logement
                  <span className="cout">
                    {state.flags.chez_les_parents === true
                      ? "chez les parents"
                      : `${nombre(chargesDuJour(state, sys))} ₵/jour`}
                  </span>
                </button>
              ) : null}
            </>
          ) : null}

          {sys.jalons && ouvert(state, "longue") ? (
            <button className="bouton discret" type="button" onClick={() => naviguer("/jalons")}>
              🏆 Jalons
            </button>
          ) : null}

          {/* ⟦SPEC-PRODUCTEUR §11.6 · A-66⟧ **Le maillon 3 de la délégation** : sans ce bouton,
              l'écran de l'équipe existe et personne ne peut l'atteindre — le défaut que l'audit
              des maillons a trouvé neuf fois. Il n'apparaît qu'une fois le producteur ACHETÉ :
              ⟦A-5⟧ « ce qui n'est pas déverrouillé n'existe pas », et un bouton grisé de plus
              dans une barre qui en compte déjà huit n'apprendrait rien. La promesse du nœud,
              elle, se voit dans l'arbre — gardée et datée ⟦A-43⟧. */}
          {equipeActive(contenu()) && producteurPossede(collectModifiers(state, contenu())) ? (
            <button
              className="bouton discret"
              type="button"
              onClick={() => naviguer("/equipe")}
              {...survolBoutonHandlers(OBJET_PAR_BOUTON_ACTION.equipe)}
            >
              🎬 L'équipe
            </button>
          ) : null}

          {/* ⟦A-4 E · M3.30⟧ **Le maillon 3 du marché des freelances** : le vivier tournait
              depuis M2.10 et rien ne l'affichait — le joueur embauchait à l'aveugle, ou pas du
              tout. Même règle que L'équipe : le bouton n'apparaît qu'une fois le marché OUVERT
              — ⟦A-78⟧ au **Grand Saut**, plus par un nœud d'arbre (*Monteur salarié* et *Le
              Studio* approfondissent désormais l'offre au lieu de l'ouvrir). Un rayon qu'on ne
              peut pas ouvrir ne se montre pas ⟦A-29⟧. */}
          {marcheOuvert(state, collectModifiers(state, contenu())) ? (
            <button
              className="bouton discret"
              type="button"
              onClick={() => naviguer("/marche")}
              {...survolBoutonHandlers(OBJET_PAR_BOUTON_ACTION.marche)}
            >
              🧑‍🔧 Le marché
            </button>
          ) : null}

          {/* ⟦A-44 · A-71⟧ **Le maillon 3 du guide** : sans ce bouton, le journal existe et
              personne ne l'atteint. Même règle que L'équipe juste au-dessus — il n'apparaît
              qu'une fois le nœud ACHETÉ (⟦A-5⟧ « ce qui n'est pas déverrouillé n'existe pas »),
              et la promesse du nœud se voit dans l'arbre, gardée et datée. */}
          {capacite(state, "guide_metube") ? (
            <button
              className="bouton discret"
              type="button"
              onClick={() => naviguer("/guide")}
              {...survolBoutonHandlers(OBJET_PAR_BOUTON_ACTION.guide)}
            >
              📰 Le guide
            </button>
          ) : null}

          {/* ⟦PLAN §4.11 · A-97⟧ Le palmarès reste au compte : un classement suppose un serveur
              et des adversaires. ⟦A-5⟧ « Ce qui n'est pas déverrouillé n'existe pas » : le
              bouton absent, jamais grisé. ⟦C23 §2⟧ « Historique » a quitté cette fenêtre : « ça
              n'a rien à faire dans le menu du bas, passe-les dans les paramètres » : le lien vit
              désormais dans Réglages, section « Ta partie » (`Reglages.tsx`). */}
          {bacASable ? null : (
            <button
              className="bouton discret"
              type="button"
              onClick={() => naviguer("/palmares")}
              {...survolBoutonHandlers(OBJET_PAR_BOUTON_ACTION.palmares)}
            >
              🏛 Palmarès
            </button>
          )}
        </fieldset>

        <ProchaineOuverture state={state} />
      </>
    );
  }

  return (
    <main className={`ecran-studio${accueilGlissement ? " interface-arrivee" : ""}`}>
      <div className="cadre-outer">
        <div className="cadre-inner">
          <header className="entete ruban-principal">
            <h1 className="entete-titre">
              <LogoMeTube className="logo-metube" />
              <span>MeTube Studio</span>
            </h1>
            <span className="entete-nom">
              <span className="entete-nom-long">
                Ma chaîne : <strong>{run.channelName}</strong>
              </span>
              <span className="entete-nom-court">{run.channelName}</span>
            </span>
            {/* L'étiquette dit **où la partie est calculée**, et elle ne ment jamais : le même
                écran n'a pas le même sens des deux côtés (une chute en mode normal termine la
                run en bac à sable, elle ramène au dernier palier côté compte, §7.3). */}
            {run.bacASable ? (
              <span className="etiquette etiquette-bac">bac à sable</span>
            ) : (
              <span className="etiquette" title="La simulation tourne sur le serveur : chaque action est écrite en base.">
                compte
              </span>
            )}
            <span className="ruban-spacer" />
            <div className="entete-droite">
              {/* ⟦A-19⟧ L'infobulle enseignait la règle d'AVANT l'avenant (« 16 h éveillées,
                  dormir ne fait pas avancer l'horloge »), soit exactement l'inverse de ce que le
                  moteur fait depuis. Les trois nombres viennent du moteur : une constante
                  recopiée ici est précisément ce qui a produit la dérive. */}
              <span
                className={`chrono${halo("chrono") ? " halo-pulse" : ""}`}
                title={`Un jour simulé = ${H_JOUR} h : ${H_EVEILLE_PAR_JOUR} h de travail + ${H_NUIT} h de nuit. Dormir coûte ${H_NUIT} h et fait avancer l'horloge — le monde tourne pendant que tu dors.`}
              >
                J{jourSimule(state.clock_h)} · {heureDuJour(state.clock_h)} h/{H_JOUR}
              </span>
              <button className="btn-icone" type="button" title="Journal" onClick={() => naviguer("/journal")}>
                📰
              </button>
              <button
                className="btn-icone"
                type="button"
                title="Réglages — tolérance de timing, accessibilité"
                onClick={() => naviguer("/reglages")}
              >
                ⚙
              </button>
            </div>
          </header>

          {/* ⟦C21 · A-146⟧ Le poste de travail : direction F3, une fenêtre pixel par bloc
              d'information. `mobile` choisit entre les fenêtres en U du bureau (≥768 px) et les
              onglets pleine chambre (<768 px) — jamais les deux montés en même temps (un seul
              `<Chambre/>`, un seul jeu de fenêtres, cf. le commentaire du hook plus haut). */}
          <div className="poste">
            {mobile ? (
              <>
                <div className="scene-mobile-zone">
                  {/* ⟦C21 ter · point 1⟧ Mobile : la pièce ENTIÈRE, jamais un recadrage `cover`
                      qui ne montrait plus qu'une bande verticale, `ajustement="contain"`,
                      `game/chambre.ts`. ⟦C26 ter · point 1⟧ `basOccupeMobile` : MÊME condition
                      que celle qui choisit quoi monter sous la pièce quelques lignes plus bas
                      (`matt ? … : ongletMobile ? … : null`) : la fenêtre Actions (ouverte par
                      défaut, ⟦C21 §1.4⟧) ou la boîte de Matt occupent le bas de l'écran, la pièce
                      s'ancre alors sous l'en-tête ; rien d'ouvert (fenêtre repliée), elle respire
                      dans tout l'espace libre. */}
                  <Chambre
                    state={state}
                    objets={objets}
                    pose={pose}
                    ajustement="contain"
                    basOccupeMobile={matt !== null || ongletMobile !== null}
                    survolExterne={survolBouton}
                  />
                </div>

                {/* ⟦C21 · A-146 §2⟧ Matt parle : la boîte prend la place du panneau (pour une
                    leçon BLOQUANTE, le jeu s'arrête réellement, la maquette F3 ne montre jamais
                    les deux à la fois, `etat1.html` n'a pas de `.panneau-mobile`). Pour une
                    réplique NON bloquante en revanche (⟦C29 · intégration · correctif tour 3⟧,
                    voir le commentaire détaillé juste plus bas sur `.pile-mobile-basse`) : la
                    boîte et le panneau s'EMPILENT dans un conteneur flex, la boîte au-dessus, le
                    panneau juste dessous, jamais l'un par-dessus l'autre. La préférence d'onglet
                    du joueur n'est jamais perdue : `ongletMobile` ne bouge pas, le panneau reste
                    affiché tel quel, puis seul une fois Matt reparti. */}
                {matt?.bloquante ? (
                  <div className={classesBulleMattMobile(true, reducedMotion)}>
                    <BoiteDialogue
                      hote={HOTE_MATT}
                      texte={matt.texte}
                      onSuite={matt.avancer}
                      actionRuban={matt.passer ? { label: "Passer", onClick: matt.passer } : undefined}
                      enfants={matt.bloquante ? <DessousDeMatt /> : undefined}
                    />
                  </div>
                ) : (
                  /* ⟦C29 · intégration · correctif tour 3⟧ **`.pile-mobile-basse` : la boîte non
                     bloquante et le panneau s'EMPILENT au lieu de se chevaucher.** Historique du
                     défaut (preuves dans `assets/making-of/2026-09-14-C29-integration/`) :
                     - Tour 1 (14/09) avait trouvé qu'un tap sur Dormir n'atteignait pas le
                       bouton (jusqu'à 6 taps réels mesurés, `repro-dormir-mobile.cjs`) — la
                       boîte non bloquante, pleine largeur au même ancrage QUE le panneau
                       Actions, le recouvrait entièrement. Corrigé alors en rendant la boîte
                       `pointer-events: none` (le tap la traversait) plutôt qu'en la repositionnant
                       (jugé fragile à l'époque, la hauteur du panneau étant dynamique).
                     - Tour 2 a vérifié ce correctif et trouvé sa conséquence non déclarée : une
                       boîte `pointer-events: none` ne peut RECEVOIR aucun tap — `apres_accueil`
                       (« Un seul geste te fait avancer : publier… ») ne se refermait donc plus
                       JAMAIS sur mobile, et réapparaissait après avoir été masquée par une leçon
                       bloquante (jamais marquée vue), cassant le point 6 du chantier (« une
                       réplique n'est jamais dite deux fois »).
                     - Tour 3 (ce correctif) résout les deux à la fois EN REPOSITIONNANT plutôt
                       qu'en neutralisant le tap : ce conteneur flex empile la boîte (si `matt`)
                       PUIS le panneau (si `ongletMobile`) du haut vers le bas, ancré comme avant
                       juste au-dessus des onglets (`app.css`, `.pile-mobile-basse`) — la boîte
                       ne recouvre donc plus jamais le panneau, un tap sur Dormir/Publier
                       l'atteint directement au premier geste (rien de la boîte n'est plus dessus,
                       la fragilité redoutée au tour 1 n'a plus lieu d'être puisque le flex
                       s'occupe lui-même de la hauteur dynamique du panneau). La boîte redevient
                       un élément normal, cliquable : `matt-mobile-bulle` n'a plus `pointer-events:
                       none` en CSS, un tap dessus la ferme comme un clic sur ordinateur
                       (`BoiteDialogue.onClick`/`auGeste`), ET `useMattChambre` referme aussi la
                       parole non bloquante en cours au premier tap ailleurs, `apres_accueil`/
                       `petit_ecran` compris désormais (`paroleNonBloquanteAffichee`, `game/
                       parole.ts`, testée) — jamais plus un tap qui la laisse bloquée affichée. */
                  <div className="pile-mobile-basse">
                    {matt ? (
                      <div className={classesBulleMattMobile(false, reducedMotion)}>
                        <BoiteDialogue
                          hote={HOTE_MATT}
                          texte={matt.texte}
                          onSuite={matt.avancer}
                          actionRuban={matt.passer ? { label: "Passer", onClick: matt.passer } : undefined}
                          enfants={matt.bloquante ? <DessousDeMatt /> : undefined}
                        />
                      </div>
                    ) : null}
                    {ongletMobile ? (
                      <div className="panneau-mobile">
                        {/* ⟦C21 bis · point 5⟧ Ex-`className={\`fen-${ongletMobile}-mobile\`}` :
                            ces trois classes (`fen-chaine-mobile`/`fen-journal-mobile`/
                            `fen-actions-mobile`) ne portaient AUCUNE règle CSS (grep sur
                            `app.css` avant retrait) — classes mortes depuis la livraison de C21,
                            retirées plutôt qu'habillées sans besoin. `.panneau-mobile .fenetre`
                            (app.css) suffit déjà à styler la fenêtre ici, quel que soit l'onglet
                            actif. */}
                        <Fenetre
                          titre={
                            ongletMobile === "chaine" ? "Ma chaîne" : ongletMobile === "journal" ? "Journal" : "Actions"
                          }
                          repliable
                          repliee={false}
                          onBasculer={() => setOngletMobile(null)}
                          id={`fen-mobile-${ongletMobile}`}
                        >
                          {ongletMobile === "chaine" ? corpsChaine : ongletMobile === "journal" ? corpsJournal : corpsActions(true)}
                        </Fenetre>
                      </div>
                    ) : null}
                  </div>
                )}

                <nav className="onglets" aria-label="Sections de la chambre">
                  <button
                    type="button"
                    className={`onglet${ongletMobile === "chaine" && !matt?.bloquante ? " actif" : ""}${ongletCibleHalo === "chaine" ? " halo-pulse" : ""}`}
                    aria-pressed={ongletMobile === "chaine" && !matt?.bloquante}
                    onClick={() => setOngletMobile((o) => (o === "chaine" ? null : "chaine"))}
                  >
                    <span className="onglet-icone" aria-hidden="true">
                      📊
                    </span>
                    Chaîne
                  </button>
                  <button
                    type="button"
                    className={`onglet${ongletMobile === "journal" && !matt?.bloquante ? " actif" : ""}${ongletCibleHalo === "journal" ? " halo-pulse" : ""}`}
                    aria-pressed={ongletMobile === "journal" && !matt?.bloquante}
                    onClick={() => setOngletMobile((o) => (o === "journal" ? null : "journal"))}
                  >
                    <span className="onglet-icone" aria-hidden="true">
                      📰
                    </span>
                    Journal
                  </button>
                  <button
                    type="button"
                    className={`onglet${ongletMobile === "actions" && !matt?.bloquante ? " actif" : ""}${ongletCibleHalo === "actions" ? " halo-pulse" : ""}`}
                    aria-pressed={ongletMobile === "actions" && !matt?.bloquante}
                    onClick={() => setOngletMobile((o) => (o === "actions" ? null : "actions"))}
                  >
                    <span className="onglet-icone" aria-hidden="true">
                      🎬
                    </span>
                    Actions
                  </button>
                </nav>
              </>
            ) : (
              <div
                className="bureau"
                style={{
                  ["--largeur-chaine" as string]: chaineRepliee ? "56px" : undefined,
                  ["--largeur-journal" as string]: journalRepliee ? "56px" : undefined,
                }}
              >
                <Fenetre
                  className="fen-chaine"
                  titre="Ma chaîne"
                  repliable
                  repliee={chaineRepliee}
                  onBasculer={() => setSetting("fenetreChaineRepliee", !chaineRepliee)}
                  id="fen-chaine"
                >
                  {corpsChaine}
                </Fenetre>

                <div className="zone-scene">
                  {/* ⟦C21 ter · point 1⟧ Ordinateur : `cover` reste (défaut de `Chambre`,
                      posé explicitement pour que les deux instances se lisent côte à côte). */}
                  <Chambre
                    state={state}
                    objets={objets}
                    pose={pose}
                    ajustement="cover"
                    survolExterne={survolBouton}
                  />
                </div>

                <Fenetre
                  className="fen-journal"
                  titre="Journal"
                  repliable
                  repliee={journalRepliee}
                  onBasculer={() => setSetting("fenetreJournalRepliee", !journalRepliee)}
                  id="fen-journal"
                >
                  {corpsJournal}
                </Fenetre>

                <div className="fen-actions-zone">
                  <Fenetre
                    className="fen-actions"
                    titre="Actions"
                    repliable
                    repliee={actionsRepliee}
                    onBasculer={() => setSetting("fenetreActionsRepliee", !actionsRepliee)}
                    id="fen-actions"
                  >
                    {corpsActions(false)}
                  </Fenetre>

                  {/* ⟦C21 · A-146 §2⟧ La boîte de Matt vient se poser AU-DESSUS de la fenêtre
                      Actions (maquette F3, `.boite-flottante`) — jamais un voile, la chambre
                      reste visible derrière. */}
                  {matt ? (
                    <div className="boite-flottante">
                      <BoiteDialogue
                        hote={HOTE_MATT}
                        texte={matt.texte}
                        onSuite={matt.avancer}
                        actionRuban={matt.passer ? { label: "Passer", onClick: matt.passer } : undefined}
                        enfants={matt.bloquante ? <DessousDeMatt /> : undefined}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ⟦A-7 décision 4⟧ Les panneaux s'ouvrent PAR-DESSUS la chambre — hors du cadre pixel,
          jamais rognés par son `overflow`. */}
      {panneauOuvert ? <Outlet /> : null}
    </main>
  );
}

/**
 * ⟦A-38⟧ **La marche** — collée sous le compteur d'abonnés, parce que c'est le même sujet.
 *
 * Le compteur dit *où tu en es*. Cette ligne dit **ce que coûte le pas suivant** — et c'est elle
 * qui manquait depuis toujours : ⟦A-35⟧ a mis la montée en puissance dans le moteur (664 points
 * et 8 vues par abonné à 100 abonnés ; 558 795 points et 224 vues au million) sans que rien à
 * l'écran ne dise que la marche avait changé de hauteur.
 *
 * Un seul chiffre, jamais deux : le HUD n'est pas un bilan comptable. Le « avant » vit dans
 * l'infobulle, pour qui va le chercher.
 *
 * ⟦A-10⟧ Aucune mécanique : les deux nombres sortent de `tauxConversion` et `prixAbonne`
 * (`game/marche.ts`). Tant qu'aucune vidéo n'est publiée, rien n'est rendu.
 */
function LaMarche({ state }: { state: GameState }) {
  const m = marche(state);
  if (!m) return null;
  const prix = Math.round(m.prix);
  return (
    <span
      className="hud-marche"
      title={
        `Un abonné se paie en vues : il t'en coûtait ${nombre(m.prixAuRepere)} à ` +
        `${nombre(m.repere)} abonnés, il t'en coûte ${nombre(prix)} aujourd'hui.`
      }
    >
      ⛰️ un abonné : <strong>{nombre(prix)}</strong> vues
    </span>
  );
}

/**
 * ⟦SPEC-MOTEUR §7.4 · A-4 arbitrage C⟧ **Le bandeau de niche** — *« bandeau “chaîne de vlog” »*,
 * mot pour mot ce que Mathieu a tranché.
 *
 * Sa place n'est pas décorative : elle est sous le compteur d'abonnés, à côté de *la marche*,
 * parce que les trois disent la même chose sous trois angles — **qui tu es**, où tu en es, ce
 * que coûte le pas suivant. Une identité éditoriale rangée dans un panneau qu'on ouvre ne serait
 * plus une identité, ce serait une statistique.
 *
 * 🔑 **C'est le maillon 3 de cette mécanique**, et il ne déclenche rien : la niche est
 * *reconnue*, jamais choisie ⟦A-4 C, « implicite »⟧. Le geste du joueur qui la produit est déjà
 * le geste central du jeu — publier. Le bandeau est donc le seul endroit où le moteur lui dit
 * qu'il a été rangé quelque part, avec la ligne de journal du tour où ça bascule.
 *
 * ⟦A-10⟧ Aucune mécanique ici, aucune écriture : `nicheReconnue` est une lecture pure de la
 * fenêtre glissante. Sans niche — touche-à-tout, ou moins de cinq vidéos — rien n'est rendu.
 */
function BandeauNiche({ state }: { state: GameState }) {
  const niche = nicheReconnue(state);
  if (niche === null) return null;
  return (
    <span
      className="hud-niche"
      title={
        "Trois de tes cinq dernières vidéos partagent ce thème : le public sait ce que fait ta " +
        "chaîne. Tant que la ligne tient, chaque vidéo de cette famille fidélise un peu plus " +
        "(et la rente de tes anciennes vidéos y gagne 15 %). Changer de sujet ne coûte rien — " +
        "on perd seulement la prime."
      }
    >
      📺 {libelleNiche(niche)}
    </span>
  );
}

/**
 * ⟦A-20⟧ **La jauge d'énergie, et sa surcharge.**
 *
 * La décision de Mathieu : *« la fatigue, c'est peut-être une jauge qui a le droit d'être
 * au-dessus de son chiffre de départ, de façon temporaire »*. Le moteur la tient depuis — la
 * grasse matinée et les boissons ouvrent le plafond jusqu'à {@link plafondEnergie} — mais
 * l'écran la **rendait invisible** : la jauge était rendue sur un max de 100 et la fraction
 * écrêtée à 1, si bien qu'à 125 ⚡ la barre était pleine, exactement comme à 100. Une mécanique
 * que le joueur ne peut pas voir n'existe pas pour lui.
 *
 * Trois choses le disent maintenant, de la plus visuelle à la plus précise :
 * 1. la piste se gradue sur le **plafond réel**, avec un trait au marqueur là où était 100 ;
 * 2. ce qui dépasse ce trait est en double hachure — « plus que plein » ;
 * 3. une ligne dit **de combien**, et qu'elle retombe.
 *
 * ⟦A-10⟧ **État neutre par défaut** : sans surcharge accordée, `plafondEnergie` vaut 100, aucun
 * repère n'est passé, la ligne n'est pas rendue — la jauge est celle d'avant, au pixel près.
 *
 * Aucun chiffre n'est écrit ici : le plafond, le seuil ordinaire et la vitesse de retombée sont
 * lus dans `@maxime/shared`.
 */
function JaugeEnergie({
  state,
  halo = false,
  nomVisible = false,
  avecSurcharge = true,
}: {
  state: GameState;
  halo?: boolean;
  /** ⟦C62 · lot tableau⟧ Le nom écrit à côté de l'icône (tableau limpide). Omis : la jauge d'avant. */
  nomVisible?: boolean;
  /**
   * ⟦C62 · lot tableau⟧ La ligne « Surcharge +10 · plafond 110 » sous la jauge. `false` : le tableau limpide la
   * range dans « Détails » (`LigneSurcharge`). Défaut `true` : le tableau d'avant.
   */
  avecSurcharge?: boolean;
}) {
  const surcharge = surchargeAccordee(state);
  const plafond = plafondEnergie(state);
  return (
    <>
      <Jauge
        icone="⚡"
        nom="Énergie"
        valeur={state.energy}
        max={plafond}
        suivi="energie"
        repere={surcharge > 0 ? ENERGIE_MAX : undefined}
        teinte="--gauge-energie"
        halo={halo}
        nomVisible={nomVisible}
        titre={
          surcharge > 0
            ? `Surcharge : ton plafond d'énergie est monté à ${plafond} ⚡. Il redescend de ${SURCHARGE_DECAY_PAR_JOUR} par jour simulé.`
            : "Ton énergie du jour : chaque action la consomme, une nuit de sommeil la restaure — contrairement à la santé mentale (❤), elle repart vite."
        }
      />
      {avecSurcharge ? <LigneSurcharge state={state} /> : null}
    </>
  );
}

/**
 * ⟦A-20⟧ La ligne d'explication de la surcharge, sous la jauge d'énergie (ou, tableau limpide ⟦C62⟧, dans
 * « Détails »). Elle n'existe QUE pendant la surcharge (⟦A-10⟧ état neutre par défaut) : un joueur qui ne dort
 * jamais deux fois d'affilée ne la voit jamais.
 */
function LigneSurcharge({ state }: { state: GameState }) {
  const surcharge = surchargeAccordee(state);
  const plafond = plafondEnergie(state);
  const excedent = Math.max(0, Math.round(state.energy) - ENERGIE_MAX);
  if (surcharge <= 0) return null;
  return (
    <p className="hud-surcharge">
      {/* ⟦C57 · lot 4⟧ Les deux nombres qui comptent ; la phrase d'avant (la règle du retour à 100, la
          décroissance par jour) rouvre derrière 📰, mot pour mot. */}
      <span>
        {excedent > 0 ? (
          <>
            ⚡ Surcharge <strong>+{excedent}</strong> · plafond <strong>{plafond}</strong>
          </>
        ) : (
          <>
            ⚡ Plafond ouvert à <strong>{plafond}</strong> (+{surcharge})
          </>
        )}
      </span>
      <LesDessous titre="La surcharge d'énergie" variante="icone" className="surcharge-dessous">
        <DessousBloc>
          <p className="dessous-texte">
            {excedent > 0
              ? `⚡ Surcharge : +${excedent} au-dessus de ${ENERGIE_MAX} · plafond ${plafond}`
              : `⚡ Plafond ouvert à ${plafond} (+${surcharge}) — de quoi remonter au-dessus de ${ENERGIE_MAX}`}{" "}
            · il retombe de {SURCHARGE_DECAY_PAR_JOUR}/jour.
          </p>
        </DessousBloc>
      </LesDessous>
    </p>
  );
}

/**
 * Le bandeau de santé mentale — et, depuis qu'ils ont un écran, l'accès aux **remèdes**.
 *
 * Il n'apparaît que hors de la zone verte, et c'est volontaire : c'est la « pédagogie par le
 * besoin » d'⟦A-2⟧, et ça préserve le départ à deux boutons d'⟦A-5⟧. Un joueur qui va bien n'a
 * pas besoin qu'on lui propose de souffler ; celui qui plonge, si — et jusqu'ici le jeu le
 * laissait plonger sans rien lui montrer, alors que le moteur avait les trois remèdes.
 */
function BandeauSM({
  state,
  onSouffler,
  survol,
}: {
  state: GameState;
  onSouffler(): void;
  /** ⟦C60⟧ Les gestionnaires de survol/focus/toucher qui allument le lit (`survolBoutonHandlers`, plus haut). */
  survol?: HTMLAttributes<HTMLButtonElement>;
}) {
  const zone = zoneSM(state.mental);
  if (zone === "vert") return null;
  return (
    <p className="bandeau-sm">
      {/* ⟦C57 · lot 4⟧ Une phrase courte ; celle d'avant, mot pour mot, derrière 📰 (collé au message). */}
      <span className="bandeau-sm-message">
        <span>{zone === "fatigue" ? "😮‍💨 Fatigue : −10 % sur ta production." : "🫠 Zone rouge : dors, ou souffle."}</span>
        <LesDessous titre={zone === "fatigue" ? "La fatigue" : "La zone rouge"} variante="icone" className="bandeau-dessous">
          <DessousBloc>
            <p className="dessous-texte">
              {zone === "fatigue"
                ? "😮‍💨 Fatigue — la qualité de ce que tu produis en prend un coup (−10 %). Va dormir."
                : "🫠 Zone rouge. Tu tiens à quoi, là ? Dors, ou prends une vraie pause."}
            </p>
          </DessousBloc>
        </LesDessous>
      </span>
      <button className="bouton discret" type="button" onClick={onSouffler} {...survol}>
        🫖 Souffler
      </button>
    </p>
  );
}

/**
 * ⟦A-5⟧ On dit qu'il reste des portes, jamais lesquelles ni ce qu'elles feront. Au tout
 * début on le dit autrement : « deux boutons, c'est normal » — sinon le joueur croit que le
 * jeu est cassé.
 */
function ProchaineOuverture({ state }: { state: GameState }) {
  // ⟦C57 · lot 4 · A-174⟧ Une phrase courte sous les boutons. ⟦C62 · A-179⟧ Plus de « Les dessous » ici : celui du
  // départ (« Deux boutons, une chambre presque vide. C'est normal : elle se remplit en jouant. ») redisait la phrase
  // d'à côté mot pour mot, celui de la suite (« La prochaine arrive à 1 000 abonnés ») la redisait aussi. Un dessous
  // qui n'a rien à ajouter n'existe pas.
  if (state.counters.videos_published === 0) {
    return (
      <p className="rappel-escalier">
        <span>Deux boutons : la chambre se remplit en jouant.</span>
      </p>
    );
  }
  const suivant = prochainEtage(state);
  if (!suivant) return <p className="rappel-escalier">Chambre complète : reste le million.</p>;
  return (
    <p className="rappel-escalier">
      <span>Prochaine ouverture : {suivant.condition}.</span>
    </p>
  );
}
